import React, { useState, useEffect } from 'react';
import { createRoot, Root } from 'react-dom/client';

interface Image {
  src: string;
  alt?: string;
}

interface ImageSliderProps {
  images: Image[];
  containerId?: string;
}

/**
 * Компонент слайдера изображений
 * Использует Progressive Enhancement:
 * - Hugo генерирует все изображения в HTML (SEO-friendly)
 * - React добавляет интерактивность поверх
 */
function ImageSlider({ images, containerId }: ImageSliderProps): JSX.Element | null {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isMounted, setIsMounted] = useState<boolean>(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const goToSlide = (index: number): void => {
    setCurrentIndex(index);
  };

  const goToPrevious = (): void => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goToNext = (): void => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Если изображений нет или компонент не смонтирован, показываем базовый HTML
  if (!images || images.length === 0 || !isMounted) {
    return null;
  }

  return (
    <div className="relative">
      {/* Контейнер слайдов */}
      <div className="relative overflow-hidden rounded-lg">
        <div 
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {images.map((image, index) => (
            <div key={index} className="min-w-full">
              <img
                src={image.src}
                alt={image.alt || `Slide ${index + 1}`}
                className="w-full h-auto object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Навигация */}
      {images.length > 1 && (
        <>
          {/* Стрелки */}
          <button
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
            aria-label="Previous slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-colors"
            aria-label="Next slide"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Индикаторы */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-white w-8' : 'bg-white/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Хранилище для уже созданных roots
const rootsMap = new WeakMap<HTMLElement, Root>();

/**
 * Инициализация слайдера
 * Ищет элементы с data-slider атрибутом и монтирует React компонент
 */
export function initImageSliders(): void {
  // Ищем все контейнеры со слайдерами
  const sliderContainers = document.querySelectorAll<HTMLElement>('[data-slider]');
  
  sliderContainers.forEach((container) => {
    // Пропускаем контейнеры, которые уже инициализированы
    if (container.dataset.reactInitialized === 'true') {
      return;
    }

    // Извлекаем данные из data-атрибутов или из существующих img элементов
    const images: Image[] = [];
    
    // Вариант 1: данные из data-атрибута (JSON)
    const imagesData = container.dataset.images;
    if (imagesData) {
      try {
        const parsed = JSON.parse(imagesData) as Image[];
        images.push(...parsed);
      } catch (e) {
        console.error('Error parsing images data:', e);
      }
    }
    
    // Вариант 2: извлекаем из существующих img элементов (для SEO)
    if (images.length === 0) {
      const imgElements = container.querySelectorAll<HTMLImageElement>('img');
      imgElements.forEach((img) => {
        images.push({
          src: img.src,
          alt: img.alt,
        });
      });
    }
    
    if (images.length > 0) {
      // Проверяем, есть ли уже root для этого контейнера
      let root = rootsMap.get(container);
      
      if (!root) {
        root = createRoot(container);
        rootsMap.set(container, root);
      }
      
      root.render(<ImageSlider images={images} containerId={container.id} />);
      
      // Помечаем контейнер как инициализированный
      container.dataset.reactInitialized = 'true';
    }
  });
}

